"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { ArrowRight, Loader2, LockKeyhole } from "lucide-react";

import { Button } from "@/components/ui/button";
import FullScreenNotice, {
  EmailSupportLink,
} from "@/components/auth/FullScreenNotice";
import SignOutLink from "@/components/auth/SignOutLink";
import { STUDIO_SUPPORT_EMAIL } from "@/lib/workspaces/suspension";

// Full-screen notice a member of a suspended studio sees in place of the app.
// Deliberately friendly and non-destructive in tone: nothing was deleted, and
// the studio comes back the moment Salesably reactivates it. Rendered by the
// root layout in the `FullScreenNotice` frame, with no page chrome around it.
export default function StudioSuspendedNotice({
  studioName,
  otherStudios = [],
  supportEmail = STUDIO_SUPPORT_EMAIL,
}) {
  const { update } = useSession();
  const [switchingTo, setSwitchingTo] = useState(null);
  const [switchFailed, setSwitchFailed] = useState(false);

  const name = studioName || "Your studio";

  // Same mechanism as the header studio switcher: move the session to the
  // other studio, then hard-navigate so every server component re-renders for
  // it (and proxy.js sees the refreshed token).
  const handleSwitch = async (studio) => {
    if (switchingTo) return;
    setSwitchingTo(studio.id);
    setSwitchFailed(false);
    try {
      await update({ workspaceId: studio.id });
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- Reload the document to discard cached data after an identity change.
      window.location.assign("/");
    } catch (error) {
      console.error("Failed to switch studio:", error);
      setSwitchFailed(true);
      setSwitchingTo(null);
    }
  };

  return (
    <FullScreenNotice
      icon={LockKeyhole}
      title={`${name} is currently inactive`}
    >
      <p className="mt-3 text-sm leading-6 text-gray-600">
        Access to this studio has been paused. Nothing has been deleted, and
        everything comes back the moment it is reactivated. To reactivate it,
        get in touch with Salesably.
      </p>

      <EmailSupportLink
        subject={`Reactivate ${name} on Salesably`}
        email={supportEmail}
      />

      {otherStudios.length > 0 && (
        <div className="mt-8 border-t border-gray-100 pt-6">
          <p className="text-sm font-medium text-gray-900">
            Your other studios are still available
          </p>
          <ul className="mt-3 space-y-2">
            {otherStudios.map((studio) => (
              <li key={studio.id}>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between text-left text-gray-900"
                  type="button"
                  onClick={() => handleSwitch(studio)}
                  disabled={Boolean(switchingTo)}
                >
                  <span>{studio.name}</span>
                  {switchingTo === studio.id ? (
                    <Loader2
                      className="h-4 w-4 animate-spin text-gray-400"
                      aria-hidden
                    />
                  ) : (
                    <ArrowRight className="h-4 w-4 text-gray-400" aria-hidden />
                  )}
                </Button>
              </li>
            ))}
          </ul>
          {switchFailed && (
            <p className="mt-2 text-xs text-red-600">
              Could not switch studios. Please try again.
            </p>
          )}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-4 border-t border-gray-100 pt-6 text-sm">
        <span className="text-gray-500">Signed in to the wrong account?</span>
        <SignOutLink />
      </div>
    </FullScreenNotice>
  );
}
